import {
  Box,
  Button,
  Group,
  Icon,
  VisuallyHidden,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import { KoOnly, type Language, useLang } from '@inflearn/i18n-tools';
import { faAngleRight } from '@inflearn/pro-regular-svg-icons';
import { isEmpty } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { fetchClientApiV1RoadmapAuthorUserId } from '../../../../resources/@types/courseSchema';
import {
  fetchClientApiV1PostsUserUserId,
  fetchClientApiV1ReviewsCourseUserUserIdReceived,
} from '../../../../resources/@types/uccSchema';
import { logger } from '../../../../resources/Logger';
import CourseCardSkeleton from '../../../components/common/CourseCardSkeleton';
import EmptyStates from '../../../components/common/EmptyStates';
import CustomHead from '../../../components/layout/CustomHead';
import Layout from '../../../components/layout/Layout';
import BlogItem from '../../../components/pages/profile/BlogItem/BlogItem';
import BlogSkeletonItem from '../../../components/pages/profile/BlogItem/BlogSkeletonItem';
import CourseItem from '../../../components/pages/profile/CourseItem/CourseItem';
import CourseReviewItem from '../../../components/pages/profile/CourseReviewItem/CourseReviewItem';
import CourseReviewSkeletonItem from '../../../components/pages/profile/CourseReviewItem/CourseReviewSkeletonItem';
import GridList from '../../../components/pages/profile/GridList/GridList';
import { IntroSectionBox } from '../../../components/pages/profile/IntroSectionBox/IntroSectionBox';
import { Introduction } from '../../../components/pages/profile/Introduce/Introduce';
import PostItem from '../../../components/pages/profile/PostItem/PostItem';
import PostSkeletonItem from '../../../components/pages/profile/PostItem/PostSkeletonItem';
import RoadmapItem from '../../../components/pages/profile/RoadmapItem/RoadmapItem';
import RoadmapSkeletonItem from '../../../components/pages/profile/RoadmapItem/RoadmapSkeletonItem';
import { DeferredComponent } from '../../../components/util/DeferredComponent';
import { INFLEARN_URL, USERS_URL } from '../../../constants/urls';
import useCheckBlackUser from '../../../hooks/useCheckBlackUser';
import useCheckMyProfile from '../../../hooks/useCheckMyProfile';
import { useHomePageReactionMutate } from '../../../hooks/useHomePageReactionMutate';
import useInstructorProfileQuery from '../../../hooks/useInstructorProfileQuery';
import useRedirectCorrectSlug from '../../../hooks/useRedirectCorrectSlug';
import useUserProfileQuery from '../../../hooks/useUserProfileQuery';
import { useLoginModalContext } from '../../../providers/ControlLoginModalContextProvider';
import { useCourseUserUserIdQuery } from '../../../queries/useCourseUserUserIdQuery';
import {
  getUsePostsUserUserIdQueryQueryKey,
  getUsePostsUserUserIdQueryQueryUrl,
  usePostsUserUserIdQuery,
} from '../../../queries/usePostsUserUserIdQuery';
import {
  getUseReviewsCourseUserUserIdReceivedQueryKey,
  getUseReviewsCourseUserUserIdReceivedQueryUrl,
  useReviewsCourseUserUserIdReceivedQuery,
} from '../../../queries/useReviewsCourseUserUserIdReceivedQuery';
import {
  getUseRoadmapAuthorUserIdQueryKey,
  getUseRoadmapAuthorUserIdQueryUrl,
  useRoadmapAuthorUserIdQuery,
} from '../../../queries/useRoadmapAuthorUserIdQuery';
import { useBlogsUserUserIdQuery } from '../../../queries/users/useBlogsUserUserIdQuery';
import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackProfileEvent } from '../../../utils/GTM/useTrackProfileEvent';
import {
  type WithCommonNextPage,
  withProfileUserProps,
} from '../../../utils/page/withProfileUserProps';
import { withRemovedPageFallback } from '../../../utils/page/withRemovedPageFallback';

const FETCH_DEFAULT_OPTION = {
  PAGE_NUMBER: 1,
  POST_ITEM_LIMIT: 5,
  ROADMAP_ITEM_LIMIT: 6,
  BLOG_ITEM_LIMIT: 6,
  COURSE_REVIEW_ITEM_LIMIT: 5,
  COURSE_ITEM_LIMIT: 6,
};

const UserProfilePage: WithCommonNextPage = () => {
  useRedirectCorrectSlug();

  const { t } = useTranslation();
  const { isKo } = useLang();

  const { query } = useRouter();
  const { data: profile } = useUserProfileQuery({ id: Number(query.id) });

  const isMounted = useRef(false);
  const { trackMixpanelEvent, isReady } = useTrackProfileEvent();
  const { open: openLoginModal } = useLoginModalContext();

  const { data: instructor } = useInstructorProfileQuery({
    userId: String(profile?.id || ''),
    isInstructor: Boolean(profile?.isInstructor),
  });
  const hasCourse = !!instructor?.courseCount;
  const hasCourseReview = !!instructor?.reviewCount;

  const {
    data: posts,
    isError: isPostsError,
    isLoading: isPostsLoading,
    refetch: postsRefetch,
  } = usePostsUserUserIdQuery({
    userId: String(profile?.id),
    queryObject: isKo ? {} : { type: 'ANSWER' },
    options: {
      keepPreviousData: true,
      enabled: Boolean(profile?.id),
    },
  });

  const {
    data: blogs,
    isError: isBlogsError,
    isLoading: isBlogsLoading,
    refetch: blogsRefetch,
  } = useBlogsUserUserIdQuery({
    userId: profile?.id ?? 0,
    options: {
      keepPreviousData: true,
      enabled: Boolean(profile?.id),
    },
  });

  const {
    data: roadmaps,
    isError: isRoadmapsError,
    isLoading: isRoadmapsLoading,
    refetch: roadmapsRefetch,
  } = useRoadmapAuthorUserIdQuery({
    userId: String(profile?.id),
    options: {
      keepPreviousData: true,
      enabled: Boolean(profile?.id),
    },
  });

  const {
    data: reviews,
    isError: isReviewsError,
    isLoading: isReviewsLoading,
    refetch: reviewsRefetch,
  } = useReviewsCourseUserUserIdReceivedQuery({
    userId: String(profile?.id),
    options: {
      keepPreviousData: true,
      enabled: hasCourseReview,
    },
  });

  const {
    data: courses,
    isError: isCourseError,
    isLoading: isCourseLoading,
    refetch: courseRefetch,
  } = useCourseUserUserIdQuery({
    userId: String(profile?.id),
    queryObject: {
      pageSize: 6,
    },
    options: {
      keepPreviousData: true,
      enabled: hasCourse,
    },
  });

  const { isMe } = useCheckMyProfile();
  const { isBlackUser } = useCheckBlackUser();

  const hasRoadmap = Boolean(roadmaps && roadmaps?.totalCount !== 0);
  const postTotalCount = posts?.totalCount ?? 0;
  const blogTotalCount = blogs?.totalCount ?? 0;
  const roadmapTotalCount = roadmaps?.totalCount ?? 0;
  const courseTotalCount = courses?.totalCount ?? 0;
  const courseReviewTotalCount = reviews?.totalCount ?? 0;

  useEffect(() => {
    if (isReady && !isMounted.current) {
      // GTM
      trackMixpanelEvent(eventNames.VIEW_PROFILE_PAGE);
      isMounted.current = true;
    }
  }, [isReady]);

  const userName = profile?.name;

  return (
    <Layout isInstructor={Boolean(profile?.isInstructor)} isInactive={Boolean(profile?.isInactive)}>
      <CustomHead
        robots={!isBlackUser}
        title={t('{{userName}}님의 소개', { userName: userName })}
        description={t(
          '인프런 {{userName}}님의 소개 페이지 입니다. - {{userName}}님 소개 | 인프런',
          { userName: userName },
        )}
        image={profile?.thumbnailUrl}
      />
      <Introduction isActiveMentor={isKo && !!instructor?.mentor?.isActive} />
      {hasCourse ? (
        <IntroSectionBox
          title={t('강의')}
          totalCount={courseTotalCount}
          styles={{ header: { marginBottom: '20px !important' } }}
        >
          <EmptyStates error={{ image: '' }} isError={isCourseError} onRetry={courseRefetch}>
            <GridList gap="48px 16px">
              {isCourseLoading ? (
                <CourseCardSkeleton count={6} />
              ) : (
                courses?.items?.map(
                  (course) =>
                    profile && (
                      <Box component="li" key={course.id} css={{ flex: '1 0 0' }}>
                        <CourseItem
                          course={course}
                          profile={profile}
                          useReactionMutate={useHomePageReactionMutate}
                          onOpenLoginModal={openLoginModal}
                        />
                      </Box>
                    ),
                )
              )}
            </GridList>
            {courseTotalCount > FETCH_DEFAULT_OPTION.COURSE_ITEM_LIMIT && (
              <ViewAllLink target={t('강의')} href={USERS_URL.COURSES(String(profile?.id))} />
            )}
          </EmptyStates>
        </IntroSectionBox>
      ) : null}
      <KoOnly>
        {hasRoadmap ? (
          <IntroSectionBox title={t('로드맵')} totalCount={roadmapTotalCount}>
            <EmptyStates isError={isRoadmapsError} error={{ image: '' }} onRetry={roadmapsRefetch}>
              <GridList>
                {isRoadmapsLoading ? (
                  <DeferredComponent>
                    <RoadmapSkeletonItem count={6} />
                  </DeferredComponent>
                ) : (
                  roadmaps?.items?.map((roadmap) => (
                    <Box component="li" key={roadmap.id} css={{ flex: '1 0 0' }}>
                      <RoadmapItem {...roadmap} />
                    </Box>
                  ))
                )}
              </GridList>
              {roadmapTotalCount > FETCH_DEFAULT_OPTION.ROADMAP_ITEM_LIMIT && (
                <ViewAllLink target={t('로드맵')} href={USERS_URL.ROADMAPS(String(profile?.id))} />
              )}
            </EmptyStates>
          </IntroSectionBox>
        ) : null}
      </KoOnly>
      {hasCourseReview ? (
        <IntroSectionBox title={t('수강평')}>
          <EmptyStates isError={isReviewsError} error={{ image: '' }} onRetry={reviewsRefetch}>
            <Box component="ul">
              {isReviewsLoading ? (
                <DeferredComponent>
                  <CourseReviewSkeletonItem count={2} />
                </DeferredComponent>
              ) : (
                reviews?.items?.map((review) => (
                  <Box
                    mb={16}
                    component="li"
                    css={{
                      [desktopFirstMediaQuery.mobile]: { marginBottom: 8 },
                    }}
                    key={review.id}
                  >
                    <CourseReviewItem {...review} />
                  </Box>
                ))
              )}
            </Box>
            {courseReviewTotalCount > FETCH_DEFAULT_OPTION.COURSE_REVIEW_ITEM_LIMIT && (
              <ViewAllLink
                target={t('수강평')}
                href={USERS_URL.COURSE_REVIEWS(String(profile?.id))}
              />
            )}
          </EmptyStates>
        </IntroSectionBox>
      ) : null}
      {isEmpty(posts?.items) ? null : (
        <IntroSectionBox
          title={t('게시글')}
          titleRightChildren={
            isMe && isEmpty(posts?.items) ? (
              <Button
                component="a"
                variant="default"
                size="sm"
                radius="md"
                href={INFLEARN_URL.COMMUNITY_QUESTIONS()}
              >
                {t('커뮤니티 둘러보기')}
              </Button>
            ) : null
          }
        >
          <EmptyStates
            isError={isPostsError}
            isEmpty={!isPostsLoading && isEmpty(posts?.items)}
            error={{ image: '' }}
            onRetry={postsRefetch}
          >
            <Box
              component="ul"
              sx={(theme) => ({
                li: {
                  borderBottom: `1px solid ${theme.colors.gray[2]}`,
                  '&:last-of-type': {
                    borderBottom: 'none',
                  },
                },
              })}
            >
              {isPostsLoading ? (
                <DeferredComponent>
                  <PostSkeletonItem count={2} />
                </DeferredComponent>
              ) : (
                posts?.items?.map((post, index) => (
                  <li key={`${post.id}${index}`}>
                    <PostItem {...post} />
                  </li>
                ))
              )}
            </Box>
            {postTotalCount > FETCH_DEFAULT_OPTION.POST_ITEM_LIMIT && (
              <ViewAllLink target={t('게시글')} href={USERS_URL.POSTS(String(profile?.id))} />
            )}
          </EmptyStates>
        </IntroSectionBox>
      )}
      <KoOnly>
        {isEmpty(blogs?.items) ? null : (
          <IntroSectionBox
            title={t('블로그')}
            totalCount={blogTotalCount}
            titleRightChildren={
              isMe && !isBlackUser ? (
                <Button
                  component="a"
                  variant="default"
                  size="sm"
                  radius="md"
                  href={INFLEARN_URL.NEW_BLOG()}
                >
                  {t('작성하기')}
                </Button>
              ) : null
            }
          >
            <EmptyStates
              isError={isBlogsError}
              isEmpty={!isBlogsLoading && isEmpty(blogs?.items)}
              error={{ image: '' }}
              onRetry={blogsRefetch}
            >
              <GridList mobileViewColumns={1} mobileGap="20px 0">
                {isBlogsLoading ? (
                  <DeferredComponent>
                    <BlogSkeletonItem count={6} />
                  </DeferredComponent>
                ) : (
                  blogs?.items?.map((blog) => (
                    <Box
                      key={blog.id}
                      component="li"
                      css={{
                        [desktopFirstMediaQuery.mobile]: {
                          padding: '20px 0',
                          borderBottom: '1px solid #E9ECEF',
                          '&:last-child': {
                            borderBottom: 'none',
                          },
                        },
                      }}
                    >
                      <BlogItem {...blog} />
                    </Box>
                  ))
                )}
              </GridList>
              {blogTotalCount > FETCH_DEFAULT_OPTION.BLOG_ITEM_LIMIT && (
                <ViewAllLink target={t('블로그')} href={USERS_URL.BLOGS(String(profile?.id))} />
              )}
            </EmptyStates>
          </IntroSectionBox>
        )}
      </KoOnly>
    </Layout>
  );
};

function ViewAllLink({ target, href }: { href: string; target: string }) {
  const { t } = useTranslation();

  return (
    <Group mt={20}>
      <Button
        component={Link}
        href={href}
        size="sm"
        m="0 auto"
        color="gray.6"
        variant="subtle"
        rightIcon={<Icon icon={faAngleRight} size="sm" color="gray.6" />}
      >
        <VisuallyHidden>
          <span>{target}</span>
        </VisuallyHidden>
        {t('전체 보기')}
      </Button>
    </Group>
  );
}

export const getServerSideProps = withProfileUserProps(
  async ({ context, queryClient, isInstructor }) => {
    try {
      const locale = context.locale as Language;
      const userId = context.params?.id ? String(context.params?.id) : '';

      await Promise.allSettled([
        queryClient.fetchQuery(
          getUsePostsUserUserIdQueryQueryKey({
            userId,
            ...(locale === 'ko' ? {} : { type: 'ANSWER' }),
          }),
          () =>
            fetchClientApiV1PostsUserUserId(
              getUsePostsUserUserIdQueryQueryUrl({
                userId,
                ...(locale === 'ko' ? {} : { type: 'ANSWER' }),
              }),
            ),
        ),
        queryClient.fetchQuery(getUseRoadmapAuthorUserIdQueryKey({ userId }), () =>
          fetchClientApiV1RoadmapAuthorUserId(getUseRoadmapAuthorUserIdQueryUrl({ userId })),
        ),
        ...(isInstructor
          ? [
              queryClient.fetchQuery(
                getUseReviewsCourseUserUserIdReceivedQueryKey({ userId }),
                () =>
                  fetchClientApiV1ReviewsCourseUserUserIdReceived(
                    getUseReviewsCourseUserUserIdReceivedQueryUrl({
                      userId,
                      lang: locale,
                    }),
                  ),
              ),
            ]
          : []),
      ]);

      return {
        props: {},
      };
    } catch (e) {
      logger.error(`[ERROR in SSR index]: ${e}`);

      return {
        props: {},
      };
    }
  },
);

export default withRemovedPageFallback(UserProfilePage);
